import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const protocolLogicCode = `// Architectural Logic: The Iteration Protocols
// 1. Iterable Protocol: Defines Symbol.iterator
// 2. Iterator Protocol: Defines next() returning { value, done }

const customCollection = {
    items: ['Node.js', 'V8', 'Libuv'],
    [Symbol.iterator]() {
        let index = 0;
        return {
            next: () => {
                const done = index >= this.items.length;
                return {
                    value: !done ? this.items[index++] : undefined,
                    done
                };
            }
        };
    }
};

// Consumption via protocols
for (const tech of customCollection) {
    console.log(tech); // Node.js, V8, Libuv
}`;

const rangeIteratorCode = `// Performance Pattern: Memory-Efficient Range
// Creating an array of 1M items is O(N) memory. 
// This iterator is O(1) memory.
class SmartRange {
    constructor(start, end) {
        this.start = start;
        this.end = end;
    }

    [Symbol.iterator]() {
        let current = this.start;
        return {
            next: () => ({
                value: current <= this.end ? current++ : undefined,
                done: current &gt; this.end + 1
            })
        };
    }
}

const largeRange = new SmartRange(1, 1_000_000);
// No memory spike! We generate values on demand.`;

const iteratorDelegationCode = `// Advanced Pattern: Iterator Delegation
function* sequence() {
    yield* [1, 2]; // Delegate to Array iterator
    yield* "AB";   // Delegate to String iterator
}

console.log([...sequence()]); // [1, 2, 'A', 'B']`;

const JSIterators = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Traversal Engineering</div>
                <h1>Iterators & Iterables</h1>
                <p className="hero-lead">
                    Master the mechanics of JavaScript traversal. Understand the underlying
                    protocols that power `for...of`, destructuring, and the spread operator
                    to build memory-efficient, custom data streams.
                </p>
            </div>

            <div className="content-section">
                <h2>The Two-Part Protocol</h2>
                <p>
                    JavaScript iteration is built on two distinct contracts. The
                    **Iterable Protocol** allows an object to define its iteration
                    behavior (via `[Symbol.iterator]`), while the **Iterator Protocol**
                    defines how values are actually produced (via the `next()` method).
                </p>
                <CodeBlock code={protocolLogicCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>O(1) Memory Traversal</h2>
                <p>
                    One of the biggest advantages of Iterators is **lazy evaluation**.
                    Instead of creating a massive array in memory, an iterator can
                    calculate the next value only when it is requested. This allows
                    us to "loop" over millions of items with virtually zero memory overhead.
                </p>
                <CodeBlock code={rangeIteratorCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Iterator Delegation</h2>
                <p>
                    JavaScript allows one iterator to "delegate" to another using
                    the `yield*` syntax (often used in Generators, which we'll cover
                    next). This is a powerful way to compose complex data streams
                    from multiple sources.
                </p>
                <CodeBlock code={iteratorDelegationCode} language="javascript" />
            </div>

            <ProTip><p>
                    When `done: true` is returned, the `value` is typically
                    discarded by language constructs like `for...of`. However,
                    the iterator protocol allows a final value to be returned
                    alongside `done: true`. Most modern JS features ignore this,
                    but some advanced generator patterns utilize it.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Protocol:** Ensure `[Symbol.iterator]` returns an object with a `next()` method.</li>
                    <li>**State:** Keep track of the iteration index in a closure or class property.</li>
                    <li>**Lazy Loading:** Use iterators for large datasets to prevent memory exhaustion.</li>
                    <li>**Compatibility:** Custom iterables automatically work with `[...]` and `destructuring`.</li>
                    <li>**Termination:** Always ensure your iterator has a clear exit condition (`done: true`).</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSIterators;




